import React, { useState } from 'react';
import { 
  Users, UserCheck, FileText, AlertTriangle, DollarSign, Smartphone,
  Brain, Shield, FileCheck, ClipboardCheck, ArrowRight, Sparkles,
  ChevronRight, Check
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const CoreModulesSection = ({ features, openBooking }) => {
  const [activeModule, setActiveModule] = useState(null);
  const [hoveredModule, setHoveredModule] = useState(null);

  const iconMap = {
    Users, UserCheck, FileText, AlertTriangle, DollarSign, Smartphone,
    Brain, Shield, FileCheck, ClipboardCheck
  };

  const defaultFeatures = [
    { id: 1, title: 'Participant Management', description: 'Complete profiles, care plans & goals', icon: 'Users', color: 'tech-blue' },
    { id: 2, title: 'Staff Management', description: 'Credentials, training & compliance', icon: 'UserCheck', color: 'care-teal' },
    { id: 3, title: 'Service Agreements', description: 'Digital contracts & signatures', icon: 'FileText', color: 'heritage-orange' },
    { id: 4, title: 'Incident Reporting', description: 'Track, escalate & report', icon: 'AlertTriangle', color: 'red-500' },
    { id: 5, title: 'Billing & Invoicing', description: 'NDIS-compliant claims', icon: 'DollarSign', color: 'purple-500' },
    { id: 6, title: 'Mobile App', description: 'Support workers in the field', icon: 'Smartphone', color: 'pink-500' },
    { id: 7, title: 'AI Compliance', description: 'Automated risk detection', icon: 'Brain', color: 'indigo-500' },
    { id: 8, title: 'Rostering', description: 'Smart shift scheduling', icon: 'ClipboardCheck', color: 'emerald-500' },
    { id: 9, title: 'Document Hub', description: 'Secure file management', icon: 'FileCheck', color: 'amber-500' },
    { id: 10, title: 'Audit Dashboard', description: 'Always audit-ready', icon: 'Shield', color: 'cyan-500' }
  ];

  const displayFeatures = features?.length >= 10 ? features.slice(0, 10) : defaultFeatures;

  const moduleDetails = {
    1: ['Full participant profiles', 'Care plan management', 'Goal tracking', 'Family portal access'],
    2: ['Credential tracking', 'Training records', 'Expiry alerts', 'Performance reviews'],
    3: ['Digital signatures', 'Template builder', 'Auto-renewals', 'Compliance validation'],
    4: ['Real-time capture', 'Auto-escalation', 'Pattern detection', 'NDIS reporting'],
    5: ['Bulk claims', 'PRODA integration', 'Payment tracking', 'Financial reports'],
    6: ['Offline mode', 'GPS check-in', 'Task management', 'Push notifications'],
    7: ['Risk detection', 'Compliance alerts', 'Predictive analytics', 'Auto-reporting'],
    8: ['Drag-drop scheduling', 'Conflict detection', 'Staff availability', 'Overtime alerts'],
    9: ['Secure storage', 'Version control', 'Expiry tracking', 'Bulk upload'],
    10: ['Compliance score', 'Gap analysis', 'Audit reports', 'Trend monitoring']
  };

  return (
    <section className="py-24 bg-gradient-to-b from-soft-sky via-page-bg to-soft-sky relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #1E6FD9 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-tech-blue/10 rounded-full border border-tech-blue/20 mb-6">
            <Sparkles className="text-tech-blue mr-2" size={18} />
            <span className="text-sm font-semibold text-tech-blue">Complete NDIS Platform</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-navy mb-4">
            Core{' '}
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-tech-blue to-care-teal bg-clip-text text-transparent">NDIS Modules</span>
              <div className="absolute -bottom-1 left-0 w-full h-3 bg-gradient-to-r from-tech-blue/20 to-care-teal/20 -skew-x-12"></div>
            </span>
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Everything you need to run a compliant, efficient NDIS organisation
          </p>
        </div>

        {/* Modules Grid - Hexagonal-inspired design */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12">
          {displayFeatures.map((feature, index) => {
            const Icon = iconMap[feature.icon] || Users;
            const isHovered = hoveredModule === feature.id;
            const isActive = activeModule === feature.id;
            
            return (
              <div
                key={feature.id || index}
                onMouseEnter={() => setHoveredModule(feature.id)}
                onMouseLeave={() => setHoveredModule(null)}
                onClick={() => setActiveModule(isActive ? null : feature.id)}
                className={`relative group cursor-pointer transition-all duration-500 ${
                  isHovered || isActive ? 'z-10' : ''
                }`}
              >
                <Card className={`overflow-hidden border-2 transition-all duration-500 ${
                  isActive 
                    ? 'border-tech-blue shadow-2xl shadow-tech-blue/20 scale-105' 
                    : isHovered 
                      ? 'border-tech-blue/50 shadow-xl scale-[1.02]' 
                      : 'border-border-line hover:border-tech-blue/30'
                }`}>
                  <CardContent className="p-5 text-center relative">
                    {/* Glow Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br from-tech-blue/5 to-care-teal/5 transition-opacity duration-500 ${
                      isHovered || isActive ? 'opacity-100' : 'opacity-0'
                    }`}></div>
                    
                    {/* Icon */}
                    <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 transition-all duration-500 ${
                      isActive 
                        ? 'bg-tech-blue scale-110 rotate-3' 
                        : isHovered 
                          ? 'bg-tech-blue/20 scale-105' 
                          : 'bg-tech-blue/10'
                    }`}>
                      <Icon className={`transition-all duration-300 ${
                        isActive ? 'text-white' : 'text-tech-blue'
                      }`} size={26} />
                      
                      {/* Pulse Ring */}
                      {isActive && (
                        <div className="absolute inset-0 rounded-2xl border-2 border-tech-blue animate-ping opacity-50"></div>
                      )}
                    </div>
                    
                    {/* Title */}
                    <h3 className={`font-bold mb-1 transition-colors ${
                      isActive ? 'text-tech-blue' : 'text-text-primary'
                    }`}>
                      {feature.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {feature.description}
                    </p>

                    {/* Module Number Badge */}
                    <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      isActive 
                        ? 'bg-tech-blue text-white' 
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {index + 1}
                    </div>

                    {/* Expand Arrow */}
                    <div className={`absolute bottom-2 right-2 transition-all duration-300 ${
                      isHovered || isActive ? 'opacity-100' : 'opacity-0'
                    }`}>
                      <ChevronRight className={`text-tech-blue transition-transform ${
                        isActive ? 'rotate-90' : ''
                      }`} size={16} />
                    </div>
                  </CardContent>
                </Card>

                {/* Expanded Detail Panel */}
                {isActive && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-tech-blue/20 p-4 z-20 animate-slideDown">
                    <div className="space-y-2">
                      {(moduleDetails[feature.id] || []).map((detail, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Check className="text-care-teal flex-shrink-0" size={14} />
                          <span className="text-sm text-text-secondary">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 bg-gradient-to-r from-tech-blue/5 to-care-teal/5 rounded-2xl border border-tech-blue/10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-tech-blue/10 flex items-center justify-center">
                <Sparkles className="text-tech-blue" size={24} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-text-primary">All modules work together</p>
                <p className="text-sm text-text-secondary">Seamless integration across your entire workflow</p>
              </div>
            </div>
            <Button 
              onClick={openBooking}
              className="bg-tech-blue hover:bg-tech-blue/90 text-white px-6 py-3"
            >
              See All Features
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </section>
  );
};

export default CoreModulesSection;
